/**
 * The effect the game falls back to when it has no file to play.
 *
 * Sound effects are optional by design: `SfxPlayer` treats a missing WAV as the
 * loss of one sound and nothing else. That is the right behaviour for a game
 * that ships without its assets, but "no sound at all" is what a player
 * actually hears on the public build, where no audio is bundled — the hit lands
 * and nothing confirms it.
 *
 * So the confirmation is *synthesised* instead. Nothing is fetched, nothing is
 * decoded, and nothing is licensed: the samples are computed here, which means
 * a clean clone with no audio in it still answers a tap with a sound.
 *
 * ## What it is not
 *
 * It is not a note, and it is not scheduled. The click is written into a plain
 * `AudioBuffer` and handed to the same one-shot path a file would take, so it
 * has no relationship to `MusicClock`, to the chart, or to judging. Replacing
 * or removing it can change what a hit sounds like and nothing else.
 *
 * ## Level
 *
 * Normalised to peak 0.85, which is where the reference `note-hit.wav` sits
 * (0.867). `DEFAULT_SFX_VOLUME` was calibrated against that peak, so the
 * built-in click lands at the same level on the bus and the setting the player
 * already has keeps meaning what it meant.
 */

/** Builds one effect's buffer. Called once, only when the file is unusable. */
export type SfxFallback = (context: BaseAudioContext) => AudioBuffer;

export type SfxFallbackMap = Readonly<Record<string, SfxFallback>>;

/** Peak the built-in effects are normalised to. See "Level" above. */
const FALLBACK_PEAK = 0.85;

/**
 * A short percussive click with a definite pitch.
 *
 * Three parts, and each is there for a reason a plain beep would miss:
 *
 *   - **two partials an octave apart** (1320 Hz and its octave), so the sound
 *     has a pitch and reads as a confirmation rather than as static;
 *   - **a noise transient** that is gone within a few milliseconds, which is
 *     what makes the attack read as a *hit* instead of a tone fading in;
 *   - **a ~1.2 ms attack ramp**, because a buffer that starts at full amplitude
 *     starts with a step, and a step is a click of the wrong kind — audible as
 *     a pop on every single note.
 *
 * Deterministic: the noise comes from a seeded generator, so the same build
 * produces the same waveform every time and a test can assert its shape.
 */
export function renderNoteHitClick(context: BaseAudioContext): AudioBuffer {
  const sampleRate = context.sampleRate;
  const seconds = 0.055;
  const length = Math.max(1, Math.round(sampleRate * seconds));
  const buffer = context.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);

  const nextNoise = seededNoise(0x9e3779b9);
  for (let index = 0; index < length; index += 1) {
    const t = index / sampleRate;
    const tone =
      Math.sin(2 * Math.PI * 1320 * t) * 0.7 + Math.sin(2 * Math.PI * 2640 * t) * 0.3;
    const body = Math.exp(-t / 0.018);
    const transient = Math.exp(-t / 0.004);
    const attack = Math.min(1, t / 0.0012);
    // And a ramp back to zero at the end, for the same reason as the attack:
    // the exponential is still at ~6% when the buffer runs out, and a buffer
    // that stops mid-amplitude stops with a step.
    const release = Math.min(1, Math.max(0, (seconds - t) / 0.008));
    data[index] = attack * release * (tone * body + nextNoise() * 0.35 * transient);
  }
  normalisePeak(data, FALLBACK_PEAK);
  return buffer;
}

/** The built-in effect for each logical name the default manifest carries. */
export const DEFAULT_SFX_FALLBACKS: SfxFallbackMap = Object.freeze({
  noteHit: renderNoteHitClick,
});

/**
 * White noise from a fixed seed.
 *
 * `Math.random` would do the job acoustically and cost the property that makes
 * this testable: two runs of the same build would produce different samples,
 * so nothing about the waveform could be asserted.
 */
function seededNoise(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let x = Math.imul(state ^ (state >>> 15), 1 | state);
    x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296 - 0.5;
  };
}

/** Scales in place so the loudest sample sits at `peak`. Silence is left alone. */
function normalisePeak(data: Float32Array, peak: number): void {
  let loudest = 0;
  for (let index = 0; index < data.length; index += 1) {
    const magnitude = Math.abs(data[index] as number);
    if (magnitude > loudest) loudest = magnitude;
  }
  if (loudest === 0) return;
  const scale = peak / loudest;
  for (let index = 0; index < data.length; index += 1) {
    data[index] = (data[index] as number) * scale;
  }
}
